import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../components/Profile.module.css';
import TrainingVariantsTable from '../../components/TrainingVariantsTable';
import { getAuthToken } from '../../lib/auth';
import { HOST } from '../../config/api';
import { useCoachOnboarding } from '../../components/coach/onboarding/CoachOnboardingProvider';
import { COACH_STEPS } from '../../components/coach/onboarding/coachOnboardingSteps';
import { AI_TRAINING_PARAMETERS_TEMPLATE, parseAiTrainingParameters } from './aiTrainingParameters';

const CoachTrainingPlansPage = ({ userId, userInfo, demoMode = false, demoData = null }) => {
    const navigate = useNavigate();
    const { setStepDone } = useCoachOnboarding();
    const [trainingVariantName, setTrainingVariantName] = useState('');
    const [trainingVariantDescription, setTrainingVariantDescription] = useState('');
    const [trainingVariantAgentDescription, setTrainingVariantAgentDescription] = useState('');
    const [trainingVariantSaved, setTrainingVariantSaved] = useState(false);
    const [trainingVariantPrivate, setTrainingVariantPrivate] = useState(true);
    const [trainingVariants, setTrainingVariants] = useState([]);
    const [isAgentDescriptionOpen, setIsAgentDescriptionOpen] = useState(false);
    const [agentDescriptionError, setAgentDescriptionError] = useState('');
    const [agentDescriptionNotice, setAgentDescriptionNotice] = useState('');
    const experimentalAiEnabled = demoMode
        ? userInfo?.experimentalAiEnabled ?? true
        : Boolean(userInfo?.experimentalAiEnabled);
    const aiGenerationEnabled = demoMode
        ? userInfo?.aiGenerationEnabled ?? true
        : Boolean(userInfo?.aiGenerationEnabled);
    const canUseAiGeneration = experimentalAiEnabled && aiGenerationEnabled;
    const coachAiParametersTemplate = userInfo?.aiParameters || AI_TRAINING_PARAMETERS_TEMPLATE;

    const fetchTrainingPlans = useCallback(async () => {
        try {
            const response = await fetch(`${HOST}/v1/training_plans?userid=${userId}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                console.error('Failed to fetch training plans:', response.statusText);
                return;
            }

            const trainingPlansResponse = await response.json();
            setTrainingVariants(trainingPlansResponse.variants || []);
        } catch (error) {
            console.error('Failed to fetch training plans:', error);
        }
    }, [navigate, userId]);

    useEffect(() => {
        if (demoMode && demoData?.trainingPlans) {
            setTrainingVariants(demoData.trainingPlans);
            return;
        }

        if (!demoMode) {
            fetchTrainingPlans();
        }
    }, [demoData, demoMode, fetchTrainingPlans]);

    const privatePlansCount = trainingVariants.filter((variant) => Boolean(variant.private)).length;
    const publicPlansCount = trainingVariants.length - privatePlansCount;

    const handleTrainingPlanSubmit = async (e) => {
        e.preventDefault();

        const agentParametersResult = parseAiTrainingParameters(trainingVariantAgentDescription);
        if (!agentParametersResult.isValid) {
            setAgentDescriptionNotice('');
            setAgentDescriptionError(agentParametersResult.error);
            setIsAgentDescriptionOpen(true);
            return;
        }

        setAgentDescriptionError('');
        setAgentDescriptionNotice(agentParametersResult.notice || '');

        if (demoMode) {
            const nextVariant = {
                id: Date.now(),
                name: trainingVariantName,
                description: trainingVariantDescription,
                agentDescription: agentParametersResult.normalizedValue,
                private: trainingVariantPrivate,
                shareCode: `demo-${Date.now()}`,
            };
            setTrainingVariants((prevState) => [nextVariant, ...prevState]);
            setTrainingVariantSaved(true);
            setTrainingVariantName('');
            setTrainingVariantDescription('');
            setTrainingVariantAgentDescription('');
            setAgentDescriptionNotice('');
            setTrainingVariantPrivate(true);
            setIsAgentDescriptionOpen(false);
            setStepDone(COACH_STEPS.PLAN_CREATED);
            return;
        }

        try {
            const payload = {
                userId,
                name: trainingVariantName,
                description: trainingVariantDescription,
                agentDescription: agentParametersResult.normalizedValue,
                private: trainingVariantPrivate,
            };

            const response = await fetch(`${HOST}/v1/training_plans`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                console.error('Failed to save training plan:', response.statusText);
                return;
            }

            setTrainingVariantSaved(true);
            setTrainingVariantName('');
            setTrainingVariantDescription('');
            setTrainingVariantAgentDescription('');
            setAgentDescriptionNotice('');
            setTrainingVariantPrivate(true);
            setIsAgentDescriptionOpen(false);
            setStepDone(COACH_STEPS.PLAN_CREATED);
            await fetchTrainingPlans();
        } catch (error) {
            console.error('Failed to save training plan:', error);
        }
    };

    const handleTrainingPlanUpdated = (updatedVariant) => {
        setTrainingVariants((prevState) =>
            prevState.map((variant) =>
                String(variant.id) === String(updatedVariant.id)
                    ? { ...variant, ...updatedVariant }
                    : variant
            )
        );
    };

    return (
        <div className={styles.profileContainer}>
            <div className={styles.profileHero}>
                <div>
                    <span className={styles.kicker}>Training plans</span>
                    <h2>Build your plan library</h2>
                    <p>
                        Create the training plans athletes subscribe to, decide what stays public or private, and
                        manage shareable structures in one place.
                    </p>
                </div>
            </div>

            <section className={styles.profileGrid}>
                <article className={`${styles.section} ${styles.sectionWide}`}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <h3 className={styles.createTrainingHeader}>Create training plan</h3>
                            <p className={styles.sectionDescription}>
                                Set the plan structure here, then add session-by-session details in Trainings.
                            </p>
                        </div>
                    </div>

                    {trainingVariantSaved && (
                        <p className={styles.variantSaved}>Training plan saved successfully.</p>
                    )}

                    <form onSubmit={handleTrainingPlanSubmit} className={styles.trainingForm}>
                        <label className={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={trainingVariantPrivate}
                                onChange={(e) => setTrainingVariantPrivate(e.target.checked)}
                                aria-label="Private"
                            />
                            <span className={styles.checkboxLabel}>Private</span>
                            <span className={styles.infoWrap}>
                                <button
                                    type="button"
                                    className={styles.info}
                                    aria-describedby="privateTip"
                                    aria-label="What does Private mean?"
                                    tabIndex={0}
                                >
                                    i
                                </button>
                                <span id="privateTip" role="tooltip" className={styles.tooltip}>
                                    Only people with the share link can follow this training plan.
                                </span>
                            </span>
                        </label>

                        <input
                            type="text"
                            id="trainingPlan"
                            placeholder="Training plan name (e.g. Veronika's freediving training plan)"
                            value={trainingVariantName}
                            onChange={(e) => setTrainingVariantName(e.target.value)}
                            className={styles.inputField}
                            required
                        />

                        <textarea
                            id="description"
                            value={trainingVariantDescription}
                            onChange={(e) => setTrainingVariantDescription(e.target.value)}
                            placeholder="Add a description ..."
                            className={styles.textArea}
                        />

                        {canUseAiGeneration ? (
                            <div className={styles.experimentalPanel}>
                                <button
                                    type="button"
                                    className={styles.experimentalToggle}
                                    onClick={() => {
                                        setIsAgentDescriptionOpen((value) => !value);
                                        setTrainingVariantAgentDescription((value) => value || coachAiParametersTemplate);
                                    }}
                                    aria-expanded={isAgentDescriptionOpen}
                                >
                                    <div>
                                        <div className={styles.experimentalPills}>
                                            <span className={styles.kicker}>AI training</span>
                                            <span className={styles.experimentalPill}>Experimental feature</span>
                                        </div>
                                        <h4>{isAgentDescriptionOpen ? 'AI training parameters' : 'Add AI training parameters'}</h4>
                                        <p className={styles.sectionDescription}>
                                            Customize the JSON parameters for this plan. Empty plans can use your coach-level AI parameters.
                                        </p>
                                    </div>
                                    <span className={`${styles.experimentalChevron} ${isAgentDescriptionOpen ? styles.experimentalChevronOpen : ''}`}>
                                        ▾
                                    </span>
                                </button>

                                {isAgentDescriptionOpen && (
                                    <textarea
                                        id="agentDescription"
                                        aria-label="AI training parameters"
                                        value={trainingVariantAgentDescription}
                                        onChange={(e) => {
                                            setTrainingVariantAgentDescription(e.target.value);
                                            setAgentDescriptionError('');
                                            const result = parseAiTrainingParameters(e.target.value);
                                            setAgentDescriptionNotice(result.notice || '');
                                        }}
                                        placeholder={coachAiParametersTemplate}
                                        className={styles.textArea}
                                    />
                                )}

                                {isAgentDescriptionOpen && agentDescriptionNotice ? (
                                    <p className={styles.fieldNotice}>{agentDescriptionNotice}</p>
                                ) : null}

                                {isAgentDescriptionOpen && agentDescriptionError ? (
                                    <p className={styles.fieldError}>{agentDescriptionError}</p>
                                ) : null}
                            </div>
                        ) : null}

                        <button type="submit" className={styles.submitButton}>
                            Save training plan
                        </button>
                    </form>
                </article>

                <article className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <h3 className={styles.createTrainingHeader}>Plan overview</h3>
                            <p className={styles.sectionDescription}>
                                Keep your publishing strategy clear while the training catalog grows.
                            </p>
                        </div>
                    </div>

                    <div className={styles.profileSummary}>
                        <div className={styles.summaryItem}>
                            <span>Coach</span>
                            <strong>{`${userInfo.firstName} ${userInfo.lastName}`}</strong>
                        </div>
                        <div className={styles.summaryItem}>
                            <span>Current mix</span>
                            <strong>
                                {trainingVariants.length === 0
                                    ? 'No plans created yet'
                                    : privatePlansCount > 0 && publicPlansCount > 0
                                        ? 'Mix of public and private plans'
                                        : privatePlansCount > 0
                                            ? 'Private plans only'
                                            : 'Public catalog only'}
                            </strong>
                        </div>
                        <div className={styles.summaryItem}>
                            <span>Next action</span>
                            <strong>
                                {trainingVariants.length === 0
                                    ? 'Create your first plan'
                                    : 'Open a plan in Trainings and add sessions'}
                            </strong>
                        </div>
                    </div>
                </article>
            </section>

            <article className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div>
                        <h3 className={styles.createTrainingHeader}>Your training plans</h3>
                        <p className={styles.sectionDescription}>
                            Open a plan in Trainings to add sessions, or copy a share link to send it directly.
                        </p>
                    </div>
                </div>
                <TrainingVariantsTable
                    variants={trainingVariants}
                    userId={userId}
                    demoMode={demoMode}
                    onVariantUpdated={handleTrainingPlanUpdated}
                    showAiFields={canUseAiGeneration}
                    aiParametersTemplate={coachAiParametersTemplate}
                />
            </article>
        </div>
    );
};

export default CoachTrainingPlansPage;
